import { Chart, Plugin, Scale } from 'chart.js';
import { SitePerformanceChartUtils } from 'app/modules/site-management/widgets/fleet-dashboard/utils/SitePerformanceChartUtils';

const SITE_PERFORMANCE_CHART_Y_SCALE_PATCH = Symbol('sitePerformanceChartYScalePatch');

type PatchedYScale = Scale & {
  [SITE_PERFORMANCE_CHART_Y_SCALE_PATCH]?: Scale['getPixelForValue'];
};

const applyBarSpacing = (chart: Chart): void => {
  const { chartArea, data } = chart;

  if (!chartArea) {
    return;
  }

  const hasPlannedSeries = data.datasets.length > 1;

  data.datasets.forEach((_dataset, datasetIndex) => {
    const meta = chart.getDatasetMeta(datasetIndex);

    if (meta.hidden) {
      return;
    }

    for (let siteIndex = 0; siteIndex < meta.data.length; siteIndex += 1) {
      meta.data[siteIndex].y = SitePerformanceChartUtils.getBarCenterY(
        siteIndex,
        datasetIndex,
        chartArea.top,
        hasPlannedSeries
      );
    }
  });
};

const patchYScale = (chart: Chart): void => {
  const yScale = chart.scales.y as PatchedYScale | undefined;

  if (!yScale) {
    return;
  }

  if (!yScale[SITE_PERFORMANCE_CHART_Y_SCALE_PATCH]) {
    yScale[SITE_PERFORMANCE_CHART_Y_SCALE_PATCH] = yScale.getPixelForValue.bind(yScale);
  }

  const originalGetPixelForValue = yScale[SITE_PERFORMANCE_CHART_Y_SCALE_PATCH];

  yScale.getPixelForValue = (value, ...args): number => {
    const categoryCount = chart.data.labels?.length ?? 0;
    const hasPlannedSeries = chart.data.datasets.length > 1;

    if (
      typeof value === 'number' &&
      Number.isInteger(value) &&
      value >= 0 &&
      value < categoryCount &&
      chart.chartArea
    ) {
      return SitePerformanceChartUtils.getGroupCenterY(value, chart.chartArea.top, hasPlannedSeries);
    }

    return originalGetPixelForValue?.(value, ...args) ?? 0;
  };
};

export const sitePerformanceChartJsBarSpacingPlugin: Plugin = {
  id: 'sitePerformanceChartJsBarSpacing',
  beforeDraw(chart: Chart): void {
    patchYScale(chart);
  },
  beforeDatasetsDraw(chart: Chart): void {
    applyBarSpacing(chart);
  },
};
